/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { styled } from '@mui/material/styles';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: 2,
    top: 10,
  },
}));

const initCart = {
  id: '',
  total: '',
  vat: '',
  amount: '',
  discount: '',
  nettotal: '',
};

const initFind = {
  search: '',
  pr: 'ALL',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  branch: 'ALL',
  date_receive: Func.dateAdd(-1),
  date_send: Func.dateNow(),
};

function PosCart({ parent }) {
  const mod = 'cart_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalAdd, setModalAdd] = useState(false);
  const [cart, setCart] = useState(initCart);
  const [cartList, setCartList] = useState([]);
  const [campaign, setCampaign] = useState([]);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    setLoading(true);

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: parent.id,
    }).then((result) => {
      if (result.status === 'ok') {
        setCart(result.parent);
        setCartList(result.data);
        setCampaign(result.campaign);
      }
      setLoading(false);
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view();
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const open = () => {
    view();
    setModalAdd(true);
  };

  const btnDel = (item) => {
    if (item.enable === 'N') {
      return (
        <Tooltip title='ขอลบ'>
          <IconButton aria-label='delete' color='error' size='small' onClick={() => del(item)}>
            <Icon>delete</Icon>
          </IconButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip title='ลบ'>
        <IconButton aria-label='delete' color='secondary' size='small' onClick={() => del(item)}>
          <Icon>delete</Icon>
        </IconButton>
      </Tooltip>
    );
  };

  const renderOrder = () => {
    return (
      <MDBox className='card' p={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell align='center' width={20}>
                  No.
                </TableCell>
                <TableCell width={100}>Barcode</TableCell>
                <TableCell>สินค้า</TableCell>
                <TableCell align='right' width={90}>
                  คงเหลือ
                </TableCell>
                <TableCell align='right' width={90}>
                  ราคา
                </TableCell>
                <TableCell align='right' width={90}>
                  จำนวน
                </TableCell>
                <TableCell align='left' width={50}>
                  หน่วย
                </TableCell>
                <TableCell align='right' width={90}>
                  รวม
                </TableCell>
                <TableCell align='right' width={40}>
                  &nbsp;
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cartList.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell align='center'>{i + 1}</TableCell>
                  <TableCell>{row.barcode}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell align='right'>
                    {row.stock_balance * 1 <= 0 ? (
                      <code>หมด</code>
                    ) : (
                      Func.numberDisplay(row.stock_balance)
                    )}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.price)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity_all)}</TableCell>
                  <TableCell align='left' className='nowrap'>
                    {row.unit_name}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.total)}</TableCell>
                  <TableCell align='center'>{btnDel(row)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderSummary = () => {
    const discountRequest = () => {
      if (cart.discount_request * 1 > 0) {
        if (cart.discount_type === 'P') {
          return <span>ขอส่วนลด {Func.numberDisplay(cart.discount_request, 2)} % :</span>;
        }

        return <span>ขอส่วนลด {Func.numberDisplay(cart.discount_request, 2)} ฿ :</span>;
      }

      return 'ขอส่วนลด :';
    };

    return (
      <MDBox className='card' p={2} mb={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='summary table'>
            <TableBody>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    มูลค่ารวม :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    {Func.numberDisplay(cart.total, 2)} ฿
                  </MDTypography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    คูปอง :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.discount_coupon * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(cart.discount_coupon, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    โปรโมชั่น :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.discount * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(cart.discount, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    {discountRequest()}
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.discount_approve * 1 > 0 ? (
                    <MDTypography variant='h4' className='nowrap'>
                      {Func.numberDisplay(cart.discount_approve, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    คืนสินค้า :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  {cart.problem_type === 'return' ? (
                    <MDTypography variant='h4' className='nowrap'>
                      - {Func.numberDisplay(cart.return_total, 2)} ฿
                    </MDTypography>
                  ) : (
                    '-'
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align='right'>
                  <MDTypography variant='h4' className='nowrap'>
                    รวมทั้งสิ้น :
                  </MDTypography>
                </TableCell>
                <TableCell align='right'>
                  <MDTypography variant='h4' color='info' className='nowrap'>
                    {Func.numberDisplay(cart.nettotal, 2)} ฿
                  </MDTypography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderCampaign = () => {
    return (
      <MDBox className='card' p={2}>
        <TableContainer sx={{ boxShadow: 0 }}>
          <Table aria-label='simple table'>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>โปรโมชั่น</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaign.map((row, i) => (
                <TableRow className='row-order' key={row.id}>
                  <TableCell width={80}>{row.code}</TableCell>
                  <TableCell>{row.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox sx={{ minHeight: 600 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            {renderOrder()}
          </Grid>
          <Grid item xs={12} md={4}>
            {renderSummary()}
            {renderCampaign()}
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog
        open={modalAdd}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-add'
        fullWidth
        onClose={() => setModalAdd(false)}
      >
        <DialogTitle>
          {parent.branch_code} : {parent.cashier_name === '' ? '-' : parent.cashier_name}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderList()}</DialogContent>
        <DialogActions>
          <Grid container>
            <Grid item xs={12}>
              <MDBox display='flex' gap={1}>
                <Tooltip title='รอบการขาย'>
                  <Chip
                    sx={{ pl: 1.5 }}
                    icon={<Icon>dvr</Icon>}
                    color='primary'
                    variant='outlined'
                    label={parent.shift_code === '' ? '-' : parent.shift_code}
                  />
                </Tooltip>
                <Tooltip title='ผู้ขาย'>
                  <Chip
                    sx={{ pl: 1.5 }}
                    icon={<Icon>account_circle</Icon>}
                    color='info'
                    variant='outlined'
                    label={parent.user_name === '' ? '-' : parent.user_name}
                  />
                </Tooltip>
                <Tooltip title='รหัสตะกร้า'>
                  <Chip
                    sx={{ pl: 1.5 }}
                    icon={<Icon>shopping_cart</Icon>}
                    color='warning'
                    variant='outlined'
                    label={parent.id === '' ? '-' : parent.id}
                  />
                </Tooltip>
              </MDBox>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {}, [parent]);

  return (
    <MDBox>
      <Tooltip title='ตะกร้าสินค้า'>
        <StyledBadge badgeContent={parent.count} color='error'>
          <IconButton aria-label='share' color='warning' onClick={open}>
            <Icon>shopping_cart</Icon>
          </IconButton>
        </StyledBadge>
      </Tooltip>
      {renderAdd()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

PosCart.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    branch_code: PropTypes.string.isRequired,
    cashier_name: PropTypes.string.isRequired,
    shift_code: PropTypes.string.isRequired,
    user_name: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
  }).isRequired,
};

export default PosCart;
